/**
 * Future Closure Form Component
 * Input form for Future Forced Closure calculation parameters
 * 
 * P2: Includes live preview of total demand
 * P3: Supports loading from case-specific configuration
 */

import React, { useState, useEffect } from 'react';
import { FutureClosureCalculationParams, CaseConfig } from '../services/futureClosureApi';
import FutureClosureLivePreview from './FutureClosureLivePreview';

interface FutureClosureFormProps {
  onCalculate: (params: FutureClosureCalculationParams) => void;
  loading: boolean;
  reportData?: any;
  initialConfig?: CaseConfig | null;
}

export default function FutureClosureForm({ onCalculate, loading, reportData, initialConfig }: FutureClosureFormProps) {
  // Basic parameters
  const [aggression, setAggression] = useState(initialConfig?.aggression ?? 50);
  const [useBestBase, setUseBestBase] = useState(initialConfig?.use_best_base ?? false);
  const [baseMonths, setBaseMonths] = useState(initialConfig?.base_months ?? 36);
  const [closureDuration, setClosureDuration] = useState(initialConfig?.closure_duration_months ?? 36);
  
  // Load initial config when it changes
  useEffect(() => {
    if (initialConfig) {
      if (initialConfig.aggression !== undefined) setAggression(initialConfig.aggression);
      if (initialConfig.use_best_base !== undefined) setUseBestBase(initialConfig.use_best_base);
      if (initialConfig.base_months !== undefined) setBaseMonths(initialConfig.base_months);
      if (initialConfig.closure_duration_months !== undefined) setClosureDuration(initialConfig.closure_duration_months);
      if (initialConfig.custom_continuations) {
        setCustomContinuations({
          officer_salary: (initialConfig.custom_continuations.officer_salary ?? 1) * 100,
          employee_wages: (initialConfig.custom_continuations.employee_wages ?? 1) * 100,
          rent: (initialConfig.custom_continuations.rent ?? 1) * 100,
          utilities: (initialConfig.custom_continuations.utilities ?? 0.25) * 100,
          insurance: (initialConfig.custom_continuations.insurance ?? 0.9) * 100,
          other_expenses: (initialConfig.custom_continuations.other_expenses ?? 0.3) * 100,
        });
        setShowAdvanced(true);
      }
    }
  }, [initialConfig]);
  
  // Advanced parameters (collapsible)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customContinuations, setCustomContinuations] = useState({
    officer_salary: 100,
    employee_wages: 100,
    rent: 100,
    utilities: 25,
    insurance: 90,
    other_expenses: 30
  });
  
  // One-time costs
  const [useCustomCosts, setUseCustomCosts] = useState(false);
  const [customShutdown, setCustomShutdown] = useState('');
  const [customRestart, setCustomRestart] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const params: FutureClosureCalculationParams = {
      aggression,
      use_best_base: useBestBase,
      base_months: baseMonths,
      closure_duration_months: closureDuration,
    };

    // Add custom continuations if in advanced mode
    if (showAdvanced) {
      params.custom_continuations = {
        officer_salary: customContinuations.officer_salary / 100,
        employee_wages: customContinuations.employee_wages / 100,
        rent: customContinuations.rent / 100,
        utilities: customContinuations.utilities / 100,
        insurance: customContinuations.insurance / 100,
        other_expenses: customContinuations.other_expenses / 100,
      };
    }

    // Add custom one-time costs if enabled
    if (useCustomCosts && customShutdown && customRestart) {
      params.custom_one_time_costs = {
        shutdown: parseFloat(customShutdown),
        restart: parseFloat(customRestart),
      };
    }

    onCalculate(params);
  };

  const getAggressionLabel = () => {
    if (aggression < 30) return 'Conservative';
    if (aggression < 70) return 'Moderate';
    return 'Aggressive';
  };

  const getAggressionColor = () => {
    if (aggression < 30) return 'text-blue-600';
    if (aggression < 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Main Parameters Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
        <h4 className="font-semibold text-gray-900 text-lg">Calculation Parameters</h4>

        {/* Aggression Slider */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Aggression Level
            </label>
            <div className="flex items-center space-x-2">
              <span className={`text-2xl font-bold ${getAggressionColor()}`}>
                {aggression}
              </span>
              <span className={`text-sm font-medium ${getAggressionColor()}`}>
                ({getAggressionLabel()})
              </span>
            </div>
          </div>
          
          <input
            type="range"
            min="0"
            max="100"
            value={aggression}
            onChange={(e) => setAggression(parseInt(e.target.value))}
            className="w-full h-3 bg-gradient-to-r from-blue-200 via-yellow-200 to-red-200 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #eab308 50%, #ef4444 100%)`
            }}
          />
          
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Conservative (0)</span>
            <span>Moderate (50)</span>
            <span>Aggressive (100)</span>
          </div>
          
          <p className="text-sm text-gray-600 mt-2">
            Controls growth rates, continuation percentages, and cost multipliers. 
            Higher = more aggressive assumptions (defensible for opening demands).
          </p>
        </div>

        {/* Base Period Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Period Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  checked={!useBestBase}
                  onChange={() => setUseBestBase(false)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Last {baseMonths} Months</p>
                  <p className="text-xs text-gray-500">Use most recent data</p>
                </div>
              </label>
              
              <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  checked={useBestBase}
                  onChange={() => setUseBestBase(true)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Best {baseMonths} Months</p>
                  <p className="text-xs text-gray-500">Highest revenue period</p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Period Length
            </label>
            <select
              value={baseMonths}
              onChange={(e) => setBaseMonths(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={12}>12 months (1 year)</option>
              <option value={24}>24 months (2 years)</option>
              <option value={36}>36 months (3 years)</option>
              <option value={48}>48 months (4 years)</option>
              <option value={60}>60 months (5 years)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Historical data period used for projections
            </p>
          </div>
        </div>

        {/* Closure Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Closure Duration (months)
          </label>
          <select
            value={closureDuration}
            onChange={(e) => setClosureDuration(parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={24}>24 months (2 years)</option>
            <option value={30}>30 months (2.5 years)</option>
            <option value={36}>36 months (3 years) - Recommended</option>
            <option value={42}>42 months (3.5 years)</option>
            <option value={48}>48 months (4 years)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Total time business will be closed for remediation
          </p>
        </div>
      </div>

      {/* Advanced Parameters (Collapsible) */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium text-gray-900">Advanced Settings</span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {showAdvanced && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 space-y-6">
            {/* Continuation Percentages */}
            <div>
              <h5 className="font-medium text-gray-900 mb-2">Expense Continuation Percentages</h5>
              <p className="text-sm text-gray-600 mb-4">
                Set what percentage of each expense category continues during the closure period. 
                These affect your total continuing expenses claim.
              </p>

              <div className="space-y-4">
                {/* Officer Salary */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-900">Officer Salary</label>
                    <span className="text-sm font-bold text-blue-600">{customContinuations.officer_salary}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={customContinuations.officer_salary}
                    onChange={(e) => setCustomContinuations({...customContinuations, officer_salary: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💼 <strong>Typically 100%.</strong> Owners/officers usually continue receiving compensation during closure to maintain business operations, manage remediation, and handle ongoing responsibilities.
                  </p>
                </div>

                {/* Employee Wages */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-900">Employee Wages</label>
                    <span className="text-sm font-bold text-blue-600">{customContinuations.employee_wages}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={customContinuations.employee_wages}
                    onChange={(e) => setCustomContinuations({...customContinuations, employee_wages: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    👥 <strong>Typically 100%.</strong> Retaining key employees prevents loss of trained staff. Businesses often pay employees during closure to ensure they're available when operations resume.
                  </p>
                </div>

                {/* Rent */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-900">Rent</label>
                    <span className="text-sm font-bold text-blue-600">{customContinuations.rent}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={customContinuations.rent}
                    onChange={(e) => setCustomContinuations({...customContinuations, rent: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    🏢 <strong>Typically 100%.</strong> Lease obligations continue regardless of business operations. Breaking a lease would result in additional costs and loss of location.
                  </p>
                </div>

                {/* Utilities */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-900">Utilities</label>
                    <span className="text-sm font-bold text-blue-600">{customContinuations.utilities}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={customContinuations.utilities}
                    onChange={(e) => setCustomContinuations({...customContinuations, utilities: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 <strong>Typically 25-50%.</strong> Basic utilities (water, minimal electricity) needed for security and maintenance. Full operations not required, so significant reduction possible.
                  </p>
                </div>

                {/* Insurance */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-900">Insurance</label>
                    <span className="text-sm font-bold text-blue-600">{customContinuations.insurance}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={customContinuations.insurance}
                    onChange={(e) => setCustomContinuations({...customContinuations, insurance: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    🛡️ <strong>Typically 90%.</strong> Property and liability insurance must continue to protect assets during closure. Some policies may allow reduced coverage, hence slight reduction.
                  </p>
                </div>

                {/* Other Expenses */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-900">Other Expenses</label>
                    <span className="text-sm font-bold text-blue-600">{customContinuations.other_expenses}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={customContinuations.other_expenses}
                    onChange={(e) => setCustomContinuations({...customContinuations, other_expenses: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    📊 <strong>Typically 30%.</strong> Includes subscriptions, maintenance contracts, professional fees, etc. Many discretionary expenses can be paused, but some fixed commitments continue.
                  </p>
                </div>
              </div>
            </div>

            {/* Custom One-Time Costs */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-900">One-Time Costs</h5>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={useCustomCosts}
                    onChange={(e) => setUseCustomCosts(e.target.checked)}
                    className="text-blue-600 focus:ring-blue-500 rounded"
                  />
                  <span className="text-sm text-gray-700">Use Custom Values</span>
                </label>
              </div>

              {useCustomCosts ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shutdown Cost ($)
                    </label>
                    <input
                      type="number"
                      value={customShutdown}
                      onChange={(e) => setCustomShutdown(e.target.value)}
                      placeholder="e.g., 250000"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      🔒 Costs to safely close operations: securing premises, inventory storage, equipment protection, notification costs, contract termination fees.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Restart Cost ($)
                    </label>
                    <input
                      type="number"
                      value={customRestart}
                      onChange={(e) => setCustomRestart(e.target.value)}
                      placeholder="e.g., 750000"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      🚀 Costs to resume operations: rehiring/training, inventory replenishment, marketing relaunch, equipment recommissioning, permits renewal.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>📊 Auto-calculated based on your financial data:</strong>
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    <li>• <strong>Shutdown:</strong> 1.5x annual discretionary expenses</li>
                    <li>• <strong>Restart:</strong> 3x annual discretionary expenses</li>
                  </ul>
                  <p className="text-xs text-blue-600 mt-2">
                    Enable "Use Custom Values" if you have specific cost estimates for your situation.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* P2: Live Preview */}
      <FutureClosureLivePreview
        aggression={aggression}
        baseMonths={baseMonths}
        useBestBase={useBestBase}
        closureDuration={closureDuration}
        customContinuations={showAdvanced ? customContinuations : undefined}
        reportData={reportData}
      />

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className={`w-full py-4 rounded-lg font-semibold text-white transition-all ${
          loading
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl'
        }`}
      >
        {loading ? (
          <span className="flex items-center justify-center space-x-2">
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Calculating...</span>
          </span>
        ) : (
          <span className="flex items-center justify-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <span>Run Calculation</span>
          </span>
        )}
      </button>
    </form>
  );
}
